import { registeredTabsHandlers } from '@/global/components/view-layouts/tabs-layout/common/registered-tabs-handlers'
import { dateService, FORMATS } from '@/global/services/date'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { api } from '@/global/services/api'
import { makeFormData } from '../../helpers/formData'
import gridView from '@/global/components/grid/GridView.vue'
import vehicleRangeLimitGridBlock from './VehicleRangeLimitGridBlock.vue'
import vehicleRangeMixin from '../../mixins/vehicleRangeMixin'
import { showDetailsAction, deleteAction, activationAction } from '@/global/components/table/helpers/tableActions'
import { getBooleanFromValue } from '@/global/utils/utils'

export default {
  name: 'RangeLimits',

  data () {
    return {
      showRangeTable: true,
      showRangeDetails: false,
      rangeDetailsTitle: this.$t('fleet/vehicle.range_limit'),
      rangeDetailsBaseTitle: this.$t('fleet/vehicle.range_limit'),
      rangeEditState: false,
      rangeCreateModeState: false,
      rangeValidationErrors: {},
      rangePermissions: {}
    }
  },

  mixins: [
    vehicleRangeMixin
  ],

  computed: {
    rangeLimitsConfig () {
      return {
        title: this.$t('fleet/vehicle_range_limit.range_limits'),
        key: 'rangeLimits',
        visible: getBooleanFromValue(this.permissions?.canReadVehicleRangeLimit),
        validationErrors: this.rangeValidationErrors,
        onTabChange: () => {
          this.showMainEditButtons = false
        },
        editUpdate: (event) => {
          const { edit } = event || {}
          this.rangeEditState = !!edit
        },
        createModeUpdate: (event) => {
          const { createMode } = event || {}
          this.rangeCreateModeState = !!createMode
        },
        tabContent: [
          // table view
          {
            component: this.tableGridComponent,
            visible: this.showRangeTable,
            apiConfig: {
              get: {
                module: 'fleet',
                route: 'vehicle-range_limit',
                queryParams: {
                  vehicle_id: this.$route.params.id
                }
              }
            },
            rowDblClick: (event) => this.onShowRangeDetails(event),
            mobileConfig: {
              title: this.$t('fleet/vehicle_range_limit.range_limits')
            },
            title_and_back_config: {
              title: this.$t('fleet/vehicle_range_limit.range_limits')
            },
            search_and_buttons_config: {
              newClick: () => this.onNewRange(),
              showNewBtn: getBooleanFromValue(this.rangePermissions?.canCreate)
            },
            gridConfig: {
              component: gridView,
              blockConfig: {
                component: vehicleRangeLimitGridBlock,
                showDetails: () => {
                  this.showRangeTable = false
                  this.showRangeDetails = true
                },
                setDetailsTitle: (val) => {
                  this.rangeDetailsTitle = val
                }
              },
              useBorder: false
            },
            onDataReceivedWithParams: (event) => {
              this.rangePermissions = event?.data?.meta?.permissions || {}
            },
            tableConfig: {
              cellsConfig: {
                input_date: {
                  custom_display_value: (item) => {
                    return item?.input_date ? dateService.formatFromUtc(item.input_date, FORMATS.date) : ''
                  }
                },
                valid_until_date: {
                  custom_display_value: (item) => {
                    return item?.valid_until_date ? dateService.formatFromUtc(item.valid_until_date, FORMATS.date) : ''
                  }
                },
                alarm_on: {
                  style: {
                    justifyItems: 'center'
                  }
                }
              },
              headers: [
                {
                  label: this.$t('fleet/vehicle_range_limit.limit_type'),
                  key: 'limit_type',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_range_limit.input_date'),
                  key: 'input_date',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_range_limit.yearly_limit'),
                  key: 'yearly_limit',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_range_limit.monthly_limit'),
                  key: 'monthly_limit',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_range_limit.valid_until_km'),
                  key: 'valid_until_km',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_range_limit.valid_until_date'),
                  key: 'valid_until_date',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_range_limit.status'),
                  key: 'status',
                  always_visible: true,
                  component: this.activeIconComponent
                },
                {
                  label: this.$t('fleet/vehicle_range_limit.alarm_on'),
                  key: 'alarm_on',
                  always_visible: true,
                  component: this.cellIconComponent,
                  style: {
                    textAlign: 'center'
                  },
                  src: (event) => {
                    const { data } = event || {}
                    return data === 1 ? '/img/icons/green-check-circle.svg' : '/img/icons/grey-check-circle.svg'
                  }
                }
              ],
              actionsConfig: [
                showDetailsAction(this.$t, (event) => this.onShowRangeDetails(event), { visible: getBooleanFromValue(this.rangePermissions?.canRead) }),
                activationAction(this.$t, (event) => this.onRangeLimitActivation(event), (event) => !!event?.data?.status),
                deleteAction(this.$t, (event) => this.onRangeLimitDelete(event), { visible: getBooleanFromValue(this.rangePermissions?.canDelete) })
              ],
              paginationConfig: {
                perPageOptions: [50, 100]
              },
              filtersConfig: {
                fields: [
                  {
                    key: 'limit_type',
                    label: this.$t('fleet/vehicle_range_limit.limit_type'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'input_date',
                    label: this.$t('fleet/vehicle_range_limit.input_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'yearly_limit',
                    label: this.$t('fleet/vehicle_range_limit.yearly_limit'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'valid_until_km',
                    label: this.$t('fleet/vehicle_range_limit.valid_until_km'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'valid_until_date',
                    label: this.$t('fleet/vehicle_range_limit.valid_until_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    visible: true,
                    editable: true
                  }
                ]
              }
            }
          },
          // details view
          {
            component: this.tabActionsComponent,
            visible: this.showRangeDetails,
            title: this.rangeDetailsTitle,
            showEditButtons: true,
            showEditBtn: getBooleanFromValue(this.rangePermissions?.canUpdate),
            showBackButton: {
              onGoBack: () => this.exitFromRangeDetails()
            },
            onCancelTabSave: () => this.exitFromRangeDetails(true),
            onSaveClick: () => this.onSaveRangeLimit()
          },
          {
            component: this.columnsContentComponent,
            visible: this.showRangeDetails,
            showBackButton: {
              useChangesNotSavedDialog: false
            },
            rows: [
              {
                disabled: !getBooleanFromValue(this.rangePermissions?.canUpdate),
                columnsCount: 3,
                fields: [
                  {
                    key: 'limit_type',
                    label: this.$t('fleet/vehicle_range_limit.limit_type'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'input_date',
                    label: this.$t('fleet/vehicle_range_limit.input_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          input_date: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'yearly_limit',
                    label: this.$t('fleet/vehicle_range_limit.yearly_limit'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'valid_until_km',
                    label: this.$t('fleet/vehicle_range_limit.valid_until_km'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'valid_until_date',
                    label: this.$t('fleet/vehicle_range_limit.valid_until_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          valid_until_date: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'alarm_on',
                    label: this.$t('fleet/vehicle_range_limit.alarm_on'),
                    type: 'base-switch',
                    visible: true,
                    editable: true
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  methods: {
    onNewRange () {
      const { setCreateMode, clearData } = registeredTabsHandlers?.rangeLimits || {}
      if (typeof setCreateMode === 'function') {
        setCreateMode(true)
      }
      if (typeof clearData === 'function') {
        clearData()
      }
      this.rangeDetailsTitle = this.$t('fleet/vehicle.new_vehicle_range_limit')
      this.showRangeTable = false
      this.showRangeDetails = true
    },

    exitFromRangeDetails (canceling = false) {
      const { clearData, setCreateMode, setEditMode, getCreateModeState, cancelSave } = registeredTabsHandlers?.rangeLimits || {}
      let isCreateMode = false
      if (typeof getCreateModeState === 'function') {
        isCreateMode = getCreateModeState()
      }

      if (isCreateMode) {
        this.showRangeTable = true
        this.showRangeDetails = false

        if (typeof clearData === 'function') {
          clearData()
        }
        if (typeof setCreateMode === 'function') {
          setCreateMode(false)
        }
        if (typeof setEditMode === 'function') {
          setEditMode(false)
        }
      }
      else if (typeof cancelSave === 'function') {
        if (!canceling) {
          this.showRangeTable = true
          this.showRangeDetails = false
        }

        cancelSave()
      }
      this.rangeValidationErrors = {}
    },

    async onSaveRangeLimit () {
      try {
        const { getFormData } = registeredTabsHandlers?.rangeLimits || {}
        const { fetchData } = registeredTableHandlers?.rangeLimits || {}
        const { id: vehicleId } = this.$route?.params || {}

        if (!vehicleId) return

        const data = typeof getFormData === 'function' ? getFormData()?.data : {}
        const formData = makeFormData(data,
          [],
          {
            vehicle_id: vehicleId
          },
          {
            input_date: {
              fromFormat: FORMATS.date,
              toFormat: FORMATS.isoDate
            },
            valid_until_date: {
              fromFormat: FORMATS.date,
              toFormat: FORMATS.isoDate
            }
          })

        let apiUrl = 'vehicle-range_limit'
        const { id: rangeLimitId } = data || {}
        if (!this.inspectionCreateModeState && rangeLimitId) {
          formData.append('_method', 'PATCH')
          apiUrl += `/${rangeLimitId}`
        }

        await api('fleet').post(apiUrl, formData)
        this.exitFromRangeDetails()

        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        this.rangeValidationErrors = exception?.response?.data?.errors || {}
        console.error(exception)
      }
    }
  }
}
